import { Link } from "react-router-dom";
import { Check, Sparkles, ArrowRight, Users, Calendar, FileText, Camera, ClipboardList, Signature, Stethoscope, Heart, LogIn, Star, Quote, Smartphone, CreditCard, Shield } from "lucide-react";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../components/LanguageSwitcher";

const FEATURES = [
  { icon: Users, key: "patients" },
  { icon: Calendar, key: "agenda" },
  { icon: Signature, key: "consent" },
  { icon: Camera, key: "photos" },
  { icon: FileText, key: "invoices" },
  { icon: ClipboardList, key: "anamnesis" },
];

export default function Landing() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-[#FAF9F6] text-stone-900" data-testid="landing-page">
      {/* Nav */}
      <nav className="sticky top-0 z-40 bg-[#FAF9F6]/85 backdrop-blur-xl border-b border-stone-200/50">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-baseline gap-2">
            <span className="font-heading italic text-2xl text-[#B07D4F]">A</span>
            <span className="font-heading text-2xl font-light tracking-tight">Ambra</span>
            <span className="text-[10px] uppercase tracking-[0.25em] text-stone-400 hidden sm:inline">patient matter</span>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Link to="/login" data-testid="landing-login-link"
              className="text-sm text-stone-700 hover:text-stone-900 border border-stone-300 hover:border-stone-500 rounded-full px-5 py-2 flex items-center gap-1.5 transition-colors">
              <LogIn className="w-3.5 h-3.5" /> {t("landing.login")}
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-96 h-96 bg-[#D4A373]/20 rounded-full blur-3xl" />
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16 lg:py-24 relative">
          <div className="max-w-3xl fade-in-up">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#D4A373]/15 text-[#B07D4F] text-xs uppercase tracking-widest mb-6">
              <Sparkles className="w-3 h-3" /> {t("landing.badge")}
            </div>
            <h1 className="font-heading text-5xl md:text-6xl font-light tracking-tight leading-[1.05] mb-6">
              {t("landing.heroTitle")}
            </h1>
            <p className="text-lg text-stone-600 max-w-2xl mb-8 leading-relaxed font-light">
              {t("landing.heroSub")}
            </p>
          </div>

          {/* Role selection cards — main CTAs */}
          <div className="grid md:grid-cols-2 gap-8 mt-14 max-w-5xl">
            {/* PROFESSIONAL */}
            <div className="group bg-white border-2 border-stone-200 hover:border-[#5F7059] rounded-3xl p-10 hover:shadow-2xl transition-all">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-2xl bg-[#8FA08B]/20 flex items-center justify-center">
                  <Stethoscope className="w-7 h-7 text-[#5F7059]" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-[0.25em] text-[#5F7059] font-semibold">
                    {t("landing.roleProfessional")}
                  </p>
                  <h3 className="font-heading text-2xl md:text-3xl font-light mt-1">{t("landing.roleProfessionalTitle")}</h3>
                </div>
              </div>
              <p className="text-stone-600 text-sm leading-relaxed mb-6">{t("landing.roleProfessionalDesc")}</p>
              <ul className="space-y-2 text-sm text-stone-700 mb-8">
                <li className="flex gap-2"><Check className="w-4 h-4 mt-0.5 text-[#8FA08B] flex-shrink-0" /> {t("landing.roleProfB1")}</li>
                <li className="flex gap-2"><Check className="w-4 h-4 mt-0.5 text-[#8FA08B] flex-shrink-0" /> {t("landing.roleProfB2")}</li>
                <li className="flex gap-2"><Check className="w-4 h-4 mt-0.5 text-[#8FA08B] flex-shrink-0" /> {t("landing.roleProfB3")}</li>
              </ul>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/login" data-testid="cta-prof-login"
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-white border-2 border-stone-900 text-stone-900 px-5 py-3 rounded-full hover:bg-stone-900 hover:text-white transition-all text-sm font-medium">
                  <LogIn className="w-4 h-4" /> {t("landing.roleProfLogin")}
                </Link>
                <Link to="/registro" data-testid="cta-professional"
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-stone-900 text-white px-5 py-3 rounded-full hover:bg-stone-700 transition-all text-sm font-medium group-hover:scale-105">
                  {t("landing.roleProfRegister")} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              <p className="text-[10px] uppercase tracking-widest text-stone-400 mt-5">{t("landing.roleProfNote")}</p>
            </div>

            {/* PATIENT */}
            <div className="group bg-gradient-to-br from-[#FFF7EF] to-white border-2 border-[#D4A373]/40 hover:border-[#B07D4F] rounded-3xl p-10 hover:shadow-2xl transition-all">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-2xl bg-[#D4A373]/25 flex items-center justify-center">
                  <Heart className="w-7 h-7 text-[#B07D4F]" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-[0.25em] text-[#B07D4F] font-semibold">
                    {t("landing.rolePatient")}
                  </p>
                  <h3 className="font-heading text-2xl md:text-3xl font-light mt-1">{t("landing.rolePatientTitle")}</h3>
                </div>
              </div>
              <p className="text-stone-600 text-sm leading-relaxed mb-6">{t("landing.rolePatientDesc")}</p>
              <ul className="space-y-2 text-sm text-stone-700 mb-8">
                <li className="flex gap-2"><Check className="w-4 h-4 mt-0.5 text-[#B07D4F] flex-shrink-0" /> {t("landing.rolePatB1")}</li>
                <li className="flex gap-2"><Check className="w-4 h-4 mt-0.5 text-[#B07D4F] flex-shrink-0" /> {t("landing.rolePatB2")}</li>
                <li className="flex gap-2"><Check className="w-4 h-4 mt-0.5 text-[#B07D4F] flex-shrink-0" /> {t("landing.rolePatB3")}</li>
              </ul>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/portal" data-testid="cta-pat-login"
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-white border-2 border-[#B07D4F] text-[#B07D4F] px-5 py-3 rounded-full hover:bg-[#B07D4F] hover:text-white transition-all text-sm font-medium">
                  <LogIn className="w-4 h-4" /> {t("landing.rolePatLogin")}
                </Link>
                <Link to="/registro-paciente" data-testid="cta-patient"
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-[#D4A373] text-stone-900 px-5 py-3 rounded-full hover:bg-[#B07D4F] hover:text-white transition-all text-sm font-medium group-hover:scale-105">
                  {t("landing.rolePatRegister")} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              <p className="text-[10px] uppercase tracking-widest text-stone-400 mt-5">{t("landing.rolePatNote")}</p>
            </div>
          </div>

          <div className="mt-12 flex flex-wrap items-center gap-6 text-xs text-stone-500">
            <span className="flex items-center gap-2"><Check className="w-4 h-4 text-[#8FA08B]" /> {t("landing.check1")}</span>
            <span className="flex items-center gap-2"><Check className="w-4 h-4 text-[#8FA08B]" /> {t("landing.check2")}</span>
            <span className="flex items-center gap-2"><Check className="w-4 h-4 text-[#8FA08B]" /> {t("landing.check3")}</span>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-20 border-t border-stone-200">
        <p className="text-xs uppercase tracking-[0.3em] text-stone-500 mb-4">{t("landing.featuresEyebrow")}</p>
        <h2 className="font-heading text-4xl md:text-5xl font-light mb-16 max-w-2xl">{t("landing.featuresTitle")}</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {FEATURES.map(({ icon: Icon, key }) => (
            <div key={key} data-testid={`feat-${key}`} className="group">
              <div className="w-12 h-12 rounded-2xl bg-[#D4A373]/15 flex items-center justify-center mb-5 group-hover:bg-[#D4A373] transition-colors">
                <Icon className="w-5 h-5 text-[#B07D4F] group-hover:text-stone-900" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-2xl font-light mb-2">{t(`landing.feat_${key}_title`)}</h3>
              <p className="text-stone-600 text-sm leading-relaxed">{t(`landing.feat_${key}_desc`)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-20 border-t border-stone-200" data-testid="landing-testimonials">
        <p className="text-xs uppercase tracking-[0.3em] text-stone-500 mb-4">{t("landing.testimonialsEyebrow")}</p>
        <h2 className="font-heading text-4xl md:text-5xl font-light mb-16 max-w-2xl">{t("landing.testimonialsTitle")}</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { name: "Dr.ssa Chiara Rossi", role: "Medico estetico · Milano", quote: t("landing.testi1") },
            { name: "Dr. Marco Bianchi", role: "Chirurgo plastico · Roma", quote: t("landing.testi2") },
            { name: "Dr.ssa Elena Conti", role: "Dermatologa · Firenze", quote: t("landing.testi3") },
          ].map((it, i) => (
            <div key={i} data-testid={`testi-${i}`}
              className="bg-white border border-stone-200 rounded-2xl p-6 flex flex-col gap-4 hover:border-[#D4A373]/60 transition-colors">
              <Quote className="w-6 h-6 text-[#D4A373]" strokeWidth={1.2} />
              <p className="text-sm text-stone-700 leading-relaxed flex-1">"{it.quote}"</p>
              <div className="flex items-center gap-1">
                {[0,1,2,3,4].map((s) => <Star key={s} className="w-3.5 h-3.5 text-[#D4A373] fill-[#D4A373]" />)}
              </div>
              <div>
                <div className="text-sm font-medium">{it.name}</div>
                <div className="text-[11px] uppercase tracking-widest text-stone-400 mt-0.5">{it.role}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Mobile mockup */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-20 border-t border-stone-200" data-testid="landing-mobile">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-stone-500 mb-4 flex items-center gap-2">
              <Smartphone className="w-3.5 h-3.5" /> {t("landing.mobileEyebrow")}
            </p>
            <h2 className="font-heading text-4xl md:text-5xl font-light mb-6 max-w-md">{t("landing.mobileTitle")}</h2>
            <p className="text-stone-600 leading-relaxed mb-6 max-w-md">{t("landing.mobileSub")}</p>
            <ul className="space-y-3 text-sm text-stone-700">
              <li className="flex gap-2"><Check className="w-4 h-4 mt-0.5 text-[#8FA08B] flex-shrink-0" /> {t("landing.mobileB1")}</li>
              <li className="flex gap-2"><Check className="w-4 h-4 mt-0.5 text-[#8FA08B] flex-shrink-0" /> {t("landing.mobileB2")}</li>
              <li className="flex gap-2"><Check className="w-4 h-4 mt-0.5 text-[#8FA08B] flex-shrink-0" /> {t("landing.mobileB3")}</li>
              <li className="flex gap-2"><Check className="w-4 h-4 mt-0.5 text-[#8FA08B] flex-shrink-0" /> {t("landing.mobileB4")}</li>
            </ul>
          </div>
          <div className="relative flex justify-center">
            <div className="absolute inset-0 bg-gradient-to-br from-[#D4A373]/20 to-transparent rounded-full blur-3xl" />
            {/* Phone frame — mostra il Pannello principale */}
            <div className="relative w-[280px] h-[560px] rounded-[42px] bg-stone-900 p-3 shadow-2xl">
              <div className="absolute top-3 left-1/2 -translate-x-1/2 w-20 h-5 bg-stone-900 rounded-b-2xl z-10" />
              <div className="w-full h-full rounded-[32px] bg-[#FAF9F6] overflow-hidden flex flex-col">
                {/* Status bar */}
                <div className="px-5 pt-2 pb-1 flex items-center justify-between text-[10px] text-stone-800 font-medium">
                  <span>09:41</span>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-1.5 rounded-sm bg-stone-800" />
                    <div className="w-4 h-2 rounded-sm border border-stone-800 ml-0.5" />
                  </div>
                </div>
                {/* App header */}
                <div className="px-5 pt-3 pb-3">
                  <div className="flex items-baseline gap-1">
                    <span className="font-heading italic text-lg text-[#B07D4F]">A</span>
                    <span className="font-heading text-lg font-light">Ambra</span>
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-stone-400 mt-2">{t("landing.mockGreeting")}</div>
                  <div className="font-heading text-xl font-light text-stone-900 mt-0.5">{t("landing.mockPanel")}</div>
                </div>
                {/* Stats grid 2x2 */}
                <div className="px-4 grid grid-cols-2 gap-2">
                  {[
                    { v: "247", l: t("landing.mockPatients"), c: "#D4A373" },
                    { v: "12", l: t("landing.mockToday"), c: "#8FA08B" },
                    { v: "38", l: t("landing.mockConsents"), c: "#B07D4F" },
                    { v: "18", l: t("landing.mockInvoices"), c: "#0B1937" },
                  ].map((s, i) => (
                    <div key={i} className="bg-white rounded-xl border border-stone-100 px-3 py-2.5">
                      <div className="w-1.5 h-1.5 rounded-full mb-1.5" style={{ backgroundColor: s.c }} />
                      <div className="font-heading text-lg font-light leading-none">{s.v}</div>
                      <div className="text-[9px] uppercase tracking-widest text-stone-400 mt-1">{s.l}</div>
                    </div>
                  ))}
                </div>
                {/* Mini calendar */}
                <div className="px-4 mt-3">
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="text-[10px] uppercase tracking-widest text-stone-500 font-medium">{t("landing.mockMonth")}</div>
                    <div className="flex gap-1">
                      <div className="w-3.5 h-3.5 rounded-full bg-stone-100 flex items-center justify-center text-[8px] text-stone-500">‹</div>
                      <div className="w-3.5 h-3.5 rounded-full bg-stone-100 flex items-center justify-center text-[8px] text-stone-500">›</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-7 gap-0.5 text-[8px] text-stone-400 uppercase tracking-widest text-center mb-1">
                    {["L","M","M","G","V","S","D"].map((d, i) => <div key={i}>{d}</div>)}
                  </div>
                  <div className="grid grid-cols-7 gap-0.5 text-[9px] text-center">
                    {[..."".padStart(6," ").split(""), ...Array.from({length: 28}, (_, i) => i+1)].map((n, i) => {
                      const isToday = n === 24;
                      const hasAppt = [3, 7, 10, 14, 18, 21, 24, 27].includes(n);
                      return (
                        <div key={i} className={`aspect-square flex items-center justify-center rounded text-[9px] ${isToday ? "bg-[#0B1937] text-white font-medium" : n ? "text-stone-700" : ""}`}>
                          {n || ""}
                          {hasAppt && !isToday && <span className="absolute w-0.5 h-0.5 rounded-full bg-[#D4A373] mt-3.5" />}
                        </div>
                      );
                    })}
                  </div>
                </div>
                {/* Bottom nav */}
                <div className="mt-auto border-t border-stone-100 px-4 py-2.5 flex justify-around">
                  {[Calendar, Users, FileText, Heart].map((Ic, i) => (
                    <div key={i} className={`w-8 h-8 rounded-lg flex items-center justify-center ${i === 0 ? "bg-[#D4A373]/20" : ""}`}>
                      <Ic className={`w-3.5 h-3.5 ${i === 0 ? "text-[#B07D4F]" : "text-stone-400"}`} strokeWidth={1.5} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Payments */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-16 border-t border-stone-200" data-testid="landing-payments">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-stone-500 mb-3 flex items-center gap-2">
              <Shield className="w-3.5 h-3.5" /> {t("landing.paymentsEyebrow")}
            </p>
            <h2 className="font-heading text-3xl md:text-4xl font-light max-w-md">{t("landing.paymentsTitle")}</h2>
          </div>
          <p className="text-sm text-stone-500 max-w-sm">{t("landing.paymentsSub")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          {[
            { label: "Visa", cls: "text-[#1A1F71]" },
            { label: "Mastercard", cls: "text-[#EB001B]" },
            { label: "American Express", cls: "text-[#006FCF]" },
            { label: "Apple Pay", cls: "text-stone-900" },
            { label: "Google Pay", cls: "text-stone-700" },
            { label: "PayPal", cls: "text-[#003087]" },
            { label: "Stripe", cls: "text-[#635BFF]" },
          ].map((p) => (
            <div key={p.label} data-testid={`pay-${p.label.toLowerCase().replace(/\s/g, "-")}`}
              className="flex items-center gap-2 bg-white border border-stone-200 rounded-xl px-5 py-3 hover:border-[#D4A373]/60 transition-colors">
              <CreditCard className={`w-4 h-4 ${p.cls}`} strokeWidth={1.5} />
              <span className={`text-sm font-medium tracking-tight ${p.cls}`}>{p.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* CTA banner */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-16">
        <div className="bg-[#0B1937] text-white rounded-3xl p-10 md:p-16 relative overflow-hidden">
          <div className="absolute -right-20 -top-20 w-72 h-72 bg-[#D4A373]/20 rounded-full blur-3xl" />
          <div className="relative">
            <p className="text-xs uppercase tracking-[0.3em] text-[#D4A373] mb-4">{t("landing.ctaEyebrow")}</p>
            <h2 className="font-heading text-4xl md:text-5xl font-light mb-6 max-w-xl">{t("landing.ctaTitle")}</h2>
            <p className="text-stone-300 mb-8 max-w-lg">{t("landing.ctaSub")}</p>
            <div className="flex flex-wrap items-center gap-4">
              <Link to="/registro" data-testid="landing-cta-btn"
                className="inline-flex items-center gap-2 bg-[#D4A373] text-stone-900 px-8 py-4 rounded-full hover:bg-white transition-colors text-sm font-medium">
                {t("landing.ctaPrimary")} <ArrowRight className="w-4 h-4" />
              </Link>
              <span className="text-xs text-stone-400">{t("landing.ctaFine")}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-stone-200 py-10">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-baseline gap-2">
            <span className="font-heading italic text-lg text-[#B07D4F]">A</span>
            <span className="font-heading text-lg font-light">Ambra</span>
            <span className="text-xs text-stone-400">— {t("landing.footerTag")}</span>
          </div>
          <div className="flex items-center gap-6 text-xs text-stone-500">
            <Link to="/login" className="hover:text-stone-900">{t("landing.login")}</Link>
            <Link to="/precios" className="hover:text-stone-900">{t("landing.viewPricing")}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
